/* eslint-disable import/no-cycle */
import { createSelector } from "reselect"
import _ from "lodash"
import fp from "lodash/fp"

import { splitsterSelector } from "common/services/utils/splitsterSelector"
import { bookingDataSelector, bookingStateSelector } from "../../../services/selector"
import { userSelector } from "common/services/user"
import { bookedAtSelector } from "common/services/branding/selectors"
import {
  getAdditionalServiceToPaySelector,
  createdAdditionalServiceToPaySelectorNonMemoized as createdAdditionalServiceToPaySelector,
} from "mmb/scenes/AccountBooking/components/Booking/scenes/AdditionalServices/services/selectors/additionalServicesSelectors"
// eslint-disable-next-line kiwicom/prefer-nitro-translate-function
import translateSelector from "common/services/intl/translateSelector"
import { ACTIONS as SHOT_ACTIONS } from "mmb/scenes/AccountBooking/components/Booking/services/scheduleChanges/services/consts"
import { getAdditionalServiceData, SERVICES } from "mmb/consts/additionalServices"
import { ACTIONS } from "mmb/consts/booking"
import { SUBMIT_MEALS_ORDER } from "../../BookingMeals/constants"
import { SUBMIT_SEATS } from "mmb/scenes/AccountBooking/components/Booking/services/additionalServices/consts"
import { SUBMIT as BOOKING_SUBMIT_INSURANCE } from "mmb/scenes/AccountBooking/components/Booking/services/bookingInsurance/consts"
import { CONFIRM as BOOKING_CHANGE_FLIGHTS } from "mmb/scenes/AccountBooking/components/Booking/services/changeFlights/consts"

const propsSelector = (state, props) => props

// $FlowFixMe
export const bookingPaymentSelector = state => state.bookingPayment

export const additionalServicePaymentPayloadSelector = createSelector(
  [
    bookingDataSelector,
    bookingPaymentSelector,
    (state, props) => props.formData,
    userSelector,
    bookedAtSelector,
    getAdditionalServiceToPaySelector,
    createdAdditionalServiceToPaySelector,
    propsSelector,
    splitsterSelector,
  ],
  (
    booking,
    bookingPayment,
    formData,
    user,
    bookedAt,
    getAdditionalServiceToPay,
    createdAdditionalServiceToPay,
    props,
    splitster,
  ) => {
    const bid = booking.id
    const additionalService =
      // $FlowFixMe
      getAdditionalServiceToPay(bookingPayment.serviceIdForPayment) ||
      createdAdditionalServiceToPay ||
      props.additionalService
    // || OrderApiCall
    const payload = {
      additionalService,
      card: {
        email: _.get(user, "email", booking.customer.email),
        number: _.get(formData, "card.number"),
        holderName: _.get(formData, "card.holderName"),
        expirationMonth: _.get(formData, "card.expiry.month"),
        expirationYear: _.get(formData, "card.expiry.year"),
        cvvNumber: _.get(formData, "card.securityCode"),
      },
      bid,
      booked_at: bookedAt,
      isSandbox: booking.sandbox,
      isPCIPayment: splitster.get("BOOKING_PAYMENT").value.includes("iframe"),
    }

    const trackablePayload = {
      additionalService,
      email: _.get(payload, "card.email"),
      bid,
      bookedAt,
      cardNumber_length: _.get(payload, "card.number.length"), // obfuscate
      cardHolderName_length: _.get(payload, "card.holderName.length"), // obfuscate
      cardExpirationMonth_length: _.get(payload, "card.expirationMonth.length"), // obfuscate
      cardExpirationYear_length: _.get(payload, "card.expirationYear.length"), // obfuscate
      cardCvvNumber_length: _.get(payload, "card.cvvNumber.length"), // obfuscate
    }

    return { payload, trackablePayload, additionalService }
  },
)

export const isMobileSummaryVisibleSelector = createSelector(
  bookingPaymentSelector,
  paymentState => Boolean(paymentState.isMobileSummaryVisible),
)

export const itemToPaySelector = createSelector(
  bookingPaymentSelector,
  paymentState => paymentState.serviceForPayment,
)

/**
 * Gets data to be inserted into the Payment modal form.
 * Returns the pending additional service for payment.
 * If no pending AS, it will return the item stored in bookingPayment.
 */
export const paymentSummarySelector = createSelector(
  [bookingPaymentSelector, getAdditionalServiceToPaySelector, translateSelector, userSelector],
  (bookingPayment, getAdditionalServiceToPay, translate, user) => {
    const { serviceForPayment, serviceIdForPayment } = bookingPayment
    // default values
    let category
    let id = 1
    let value = 0
    let margin = 0
    let credits = 0
    let currency = ""
    let gate

    // check for pending additional service with status open
    const unpaidAS = getAdditionalServiceToPay(serviceIdForPayment)

    if (unpaidAS) {
      // Existing service - created by CS
      category = unpaidAS.category
      value = unpaidAS.payment.amount
      currency = unpaidAS.payment.currency
      margin = unpaidAS.payment.margin || 0
      credits = user ? user.getBalance(currency) : 0
      // gate not always set to credits correctly
      gate = unpaidAS.payment.credits > 0 ? "credits" : unpaidAS.payment.gate
      id = unpaidAS.id
    } else {
      // serviceForPayment - a newly created AS request
      category = serviceForPayment.category
      value = serviceForPayment.price
      margin = serviceForPayment.margin
      currency = serviceForPayment.currency
      credits = user ? user.getBalance(serviceForPayment.currency) : 0
    }

    return {
      summary: {
        displayItems: [
          {
            id,
            label: translate(getAdditionalServiceData(category).name),
            amount: {
              value,
              currency,
              margin,
            },
            category,
          },
        ],
        total: {
          label: translate("booking.payment.total"),
          amount: {
            value,
            currency,
            margin: 0, // only adding because of flow
          },
        },
      },
      credits,
      gate,
    }
  },
)
export const isPaymentSuccessfulSelector = createSelector(
  bookingStateSelector,
  (bookingState): boolean =>
    Boolean(
      bookingState.success && bookingState.success.action === ACTIONS.ADDITIONAL_SERVICE_PAYMENT,
    ),
)
export const successfulPaymentMessageSelector = createSelector(
  [isPaymentSuccessfulSelector, bookingStateSelector],
  (isSuccessful, bookingState): string => {
    if (isSuccessful) {
      switch (fp.get("additionalService.category", bookingState.success.payload)) {
        case SERVICES.BAGGAGE:
          return __("account.payment_success_add_bags")
        case SERVICES.CHANGE_FLIGHTS:
          return __("account.payment_success_change_journey")
        case SERVICES.PRICE_CHANGE:
          return __("account.payment_success_price_change")
        case SERVICES.ASSISTED_REFUND:
          return __("refund.thankyou.text_assisted")
        default:
          return __("account.payment_success_general")
      }
    }

    return ""
  },
)
export const isPaymentErrorSelector = createSelector(
  bookingStateSelector,
  (bookingState): boolean =>
    Boolean(
      bookingState.error &&
        [
          ACTIONS.ADDITIONAL_SERVICE_PAYMENT,
          BOOKING_SUBMIT_INSURANCE,
          ACTIONS.CHANGE_BAGS,
          BOOKING_CHANGE_FLIGHTS,
          SUBMIT_SEATS,
          SHOT_ACTIONS.REQUEST_RESOLUTION,
          SUBMIT_MEALS_ORDER,
        ].includes(bookingState.error.action),
    ),
)
export const shouldReloadBookingUponErrorPaymentSelector = createSelector(
  bookingStateSelector,
  (bookingState): boolean =>
    Boolean(
      bookingState.error && [SHOT_ACTIONS.REQUEST_RESOLUTION].includes(bookingState.error.action), // SHOT is handling error on it's own
    ),
)
export const errorMessageSelector = createSelector(
  bookingStateSelector,
  (bookingState): string =>
    fp.get(["error", "error", "msg"], bookingState) ||
    fp.get(["error", "error"], bookingState) ||
    "",
)

export const cardTypeSelector = createSelector(
  bookingPaymentSelector,
  paymentState => paymentState.cardType,
)
